import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, startTransition } from 'react';

const AuthContext = createContext();

const TOKEN_KEY = 'adminToken';
const removeStoredToken = () => {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* blocked storage must not prevent logout */ }
};

/** Where the worker lives; relative "/api/…" calls resolve to the same origin. */
const API_ORIGIN = 'https://nycroyalcarriage.com';

/**
 * Headers for an admin API call. Attaches the session token so the worker can
 * prove the caller is a logged-in admin (booking list/update/delete, stats,
 * customers, exports … are all gated on it server-side).
 *
 *   fetch('/api/booking-api?limit=50', { headers: adminHeaders() })
 *   fetch(url, { method: 'PUT', headers: adminHeaders({ 'Cache-Control': 'no-cache' }), body })
 */
export const adminHeaders = (extra = {}) => {
  let token = null;
  try { token = localStorage.getItem(TOKEN_KEY); } catch (e) { /* storage blocked */ }
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const isApiRequest = (input) => {
  const url = typeof input === 'string' ? input : (input && input.url) || '';
  if (url.startsWith('/api/')) return true;
  if (url.startsWith(API_ORIGIN + '/api/')) return true;
  try {
    if (typeof window !== 'undefined' && url.startsWith(window.location.origin + '/api/')) return true;
  } catch (e) { /* ignore */ }
  return false;
};

/**
 * Admin screens make well over a hundred raw fetch('/api/…') calls, most of
 * them written before the worker checked identity at all. Rather than trust
 * that every one of them was found and edited, the provider installs ONE
 * interceptor while an admin session exists: any same-app /api/ request that
 * does not already carry an Authorization header gets the Bearer token. The
 * explicit adminHeaders() above remains the preferred form for new code.
 */
let fetchPatched = false;
const installAdminFetch = () => {
  if (fetchPatched || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const original = window.fetch.bind(window);
  window.fetch = (input, init) => {
    try {
      let token = null;
      try { token = localStorage.getItem(TOKEN_KEY); } catch (e) { /* ignore */ }
      if (token && isApiRequest(input)) {
        const headers = new Headers((init && init.headers) || (input instanceof Request ? input.headers : undefined));
        if (!headers.has('Authorization')) {
          headers.set('Authorization', `Bearer ${token}`);
          return original(input, { ...(init || {}), headers });
        }
      }
    } catch (e) { /* fall through to the untouched call */ }
    return original(input, init);
  };
  fetchPatched = true;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null); // Store user info including role
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    installAdminFetch();
    let savedToken = null;
    try { savedToken = localStorage.getItem(TOKEN_KEY); } catch { /* public pages work without storage */ }
    if (savedToken) {
      verifyToken(savedToken);
    } else {
      startTransition(() => setLoading(false));
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await fetch('/api/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'verify',
          token: tokenToVerify
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          setToken(tokenToVerify);
          setUser(data.user); // Store user info including role
          setIsAuthenticated(true);
        } else {
          removeStoredToken();
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        removeStoredToken();
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      removeStoredToken();
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await fetch('/api/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'login',
          username,
          password
        })
      });

      // Locked out after too many failures: say so, or the right password looks wrong too.
      if (response.status === 429) return { success: false, error: (await response.json()).error };
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          localStorage.setItem('adminToken', data.token);
          setToken(data.token);
          setUser(data.user); // Store user info including role
          setIsAuthenticated(true);
          return { success: true, user: data.user };
        }
      }

      return { success: false, error: 'Invalid credentials' };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: 'Login failed. Please try again.' };
    }
  };

  const logout = useCallback(() => {
    removeStoredToken();
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff';

  const value = useMemo(() => ({
    isAuthenticated,
    token,
    user,
    loading,
    login,
    logout,
    isAdmin,
    isStaff
  }), [isAuthenticated, token, user, loading, login, logout, isAdmin, isStaff]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
